import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { fetchIntegrationsByType } from "../integrations/api";
import bettercontactLogo from "../../img/bettercontact.png";
import { INTEGRATION_TYPE_BETTERCONTACT } from "../../constants";

interface BetterContactIntegration {
  id: string;
  type: string;
  name: string;
  instanceName?: string;
}

export default ({
  actionParameters,
  setActionParameters,
  setValidForm,
  getToken,
  companyId,
}: {
  actionParameters: any;
  setActionParameters: (p: any) => void;
  setValidForm: (b: boolean) => void;
  getToken: () => Promise<string>;
  companyId: string;
}) => {
  const [bettercontactIntegrations, setBettercontactIntegrations] = useState<
    BetterContactIntegration[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch Better Contact integrations
  useEffect(() => {
    if (!getToken || !companyId) return;

    const loadIntegrations = async () => {
      setLoading(true);
      setError(null);
      try {
        const integrations = await fetchIntegrationsByType(
          companyId,
          INTEGRATION_TYPE_BETTERCONTACT,
          getToken
        );
        setBettercontactIntegrations(integrations || []);

        // Auto-select if there's only one integration and no value selected
        if (
          integrations.length === 1 &&
          !actionParameters.bettercontactIntegrationId
        ) {
          setActionParameters({
            ...actionParameters,
            bettercontactIntegrationId: integrations[0].id,
          });
        }
      } catch (err) {
        console.error("Error fetching Better Contact integrations:", err);
        setError("Failed to load Better Contact integrations");
        setBettercontactIntegrations([]);
      } finally {
        setLoading(false);
      }
    };

    loadIntegrations();
  }, [getToken, companyId]);

  // Validate form - integration must be selected and at least one enrichment type enabled
  useEffect(() => {
    const hasIntegration = Boolean(actionParameters.bettercontactIntegrationId);
    const hasEnrichmentType = actionParameters.enrichEmail || actionParameters.enrichPhone;
    const isValid = hasIntegration && hasEnrichmentType;
    setValidForm(isValid);
  }, [actionParameters.bettercontactIntegrationId, actionParameters.enrichEmail, actionParameters.enrichPhone, setValidForm]);

  const handleIntegrationChange = (event: SelectChangeEvent<string>) => {
    setActionParameters({
      ...actionParameters,
      bettercontactIntegrationId: event.target.value,
    });
  };

  const handleEnrichEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActionParameters({
      ...actionParameters,
      enrichEmail: event.target.checked,
    });
  };

  const handleEnrichPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActionParameters({
      ...actionParameters,
      enrichPhone: event.target.checked,
    });
  };

  const getIntegrationDisplayName = (
    integration: BetterContactIntegration
  ): string => {
    return integration.instanceName || integration.name || "Better Contact";
  };

  const renderValue = (selectedId: string) => {
    if (!selectedId) {
      return <em>Select a Better Contact integration</em>;
    }
    const integration = bettercontactIntegrations.find((i) => i.id === selectedId);
    if (!integration) {
      return <em>Select a Better Contact integration</em>;
    }
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Avatar
          src={bettercontactLogo}
          sx={{ width: 24, height: 24 }}
          variant="rounded"
        />
        <span>{getIntegrationDisplayName(integration)}</span>
      </Box>
    );
  };

  return (
    <div>
      <div style={{ marginTop: "24px" }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
        >
          Better Contact Integration
          <Tooltip title="Select which Better Contact integration to use for enrichment. The action will find verified emails and phone numbers using your Better Contact API key.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && bettercontactIntegrations.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No Better Contact integrations found. Please set up a Better Contact
            integration in the Integrations settings first.
          </Alert>
        )}

        <FormControl
          fullWidth
          disabled={loading || bettercontactIntegrations.length === 0}
          required
        >
          <InputLabel>Better Contact Integration</InputLabel>
          <Select
            value={actionParameters.bettercontactIntegrationId || ""}
            onChange={handleIntegrationChange}
            label="Better Contact Integration"
            renderValue={renderValue}
            startAdornment={
              loading ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : undefined
            }
          >
            {loading && (
              <MenuItem value="" disabled>
                Loading integrations...
              </MenuItem>
            )}
            {!loading && bettercontactIntegrations.length === 0 && (
              <MenuItem value="" disabled>
                No Better Contact integrations available
              </MenuItem>
            )}
            {bettercontactIntegrations.map((integration) => (
              <MenuItem key={integration.id} value={integration.id}>
                <ListItemAvatar sx={{ minWidth: 40 }}>
                  <Avatar
                    src={bettercontactLogo}
                    sx={{ width: 28, height: 28 }}
                    variant="rounded"
                  />
                </ListItemAvatar>
                <ListItemText primary={getIntegrationDisplayName(integration)} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Contacts will be enriched using this Better Contact account
          </FormHelperText>
        </FormControl>
      </div>

      <div style={{ marginTop: "24px" }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
        >
          Enrichment Options
          <Tooltip title="Select which data types to enrich. You must enable at least one option.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={actionParameters.enrichEmail ?? true}
                onChange={handleEnrichEmailChange}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1">Enrich Email Addresses</Typography>
                <Typography variant="body2" color="text.secondary">
                  Find verified business email addresses
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={actionParameters.enrichPhone ?? false}
                onChange={handleEnrichPhoneChange}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1">Enrich Phone Numbers</Typography>
                <Typography variant="body2" color="text.secondary">
                  Find mobile and direct phone numbers
                </Typography>
              </Box>
            }
          />
        </Box>

        {!actionParameters.enrichEmail && !actionParameters.enrichPhone && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Please enable at least one enrichment type (Email or Phone).
          </Alert>
        )}
      </div>

      <div style={{ marginTop: "24px" }}>
        <Typography variant="subtitle1">Required Fields</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          To enrich a contact, your list must have these fields:
        </Typography>
        <ul
          style={{
            marginTop: "8px",
            paddingLeft: "20px",
            color: "text.secondary",
          }}
        >
          <li>
            <strong>First Name</strong>
          </li>
          <li>
            <strong>Last Name</strong>
          </li>
          <li>
            <strong>Company Name</strong> OR <strong>Company Domain/Website</strong>
          </li>
          <li>
            <strong>LinkedIn URL</strong> (optional, improves match rate)
          </li>
        </ul>
      </div>

      <div style={{ marginTop: "24px" }}>
        <Typography variant="subtitle1">Fields Enriched</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The following fields will be populated (if found and currently empty):
        </Typography>
        <Box sx={{ display: "flex", gap: 4, mt: 1 }}>
          <ul
            style={{
              marginTop: "8px",
              paddingLeft: "20px",
              color: "text.secondary",
            }}
          >
            {actionParameters.enrichEmail && <li>Email (verified)</li>}
            {actionParameters.enrichPhone && (
              <>
                <li>Phone Number (mobile)</li>
                <li>Phone Number (direct)</li>
              </>
            )}
          </ul>
        </Box>
      </div>

      <div style={{ marginTop: "24px" }}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Note:</strong> Better Contact uses waterfall enrichment with 20+ data providers
            for maximum match rates. Results may take a few minutes as they are processed asynchronously.
          </Typography>
        </Alert>
      </div>
    </div>
  );
};
